import React from 'react'

import { dateFormat } from '@sketch/utils'
import { PLAN_TYPE_LABEL } from '../../utils'

import { Pluralize } from '@sketch/components'
import BillingInfoTable from '../BillingInfoTable'

import { ErrorPill, Description, StyledButton } from './BillingCycle.styles'

import {
  BillingSeatsInfoFragment,
  CloudBillingPlanFragment,
} from '@sketch/gql-types'

interface BillingCycleProps {
  subscriptionEnd?: string
  nextBillingCycleDate?: string
  seats: BillingSeatsInfoFragment
  isPartner?: boolean
  isPartneredWorkspace?: boolean
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: CloudBillingPlanFragment

  onCancelSeatScheduleChange: () => void
}

const getHeaderCopy = (
  isWorkspaceOnTrial: boolean,
  isEducationPlan: boolean
) =>
  isWorkspaceOnTrial || isEducationPlan
    ? 'First Billing Date'
    : 'Next Billing Date'

/**
 * BillingCycle
 *
 * Rends the "Next Billing Cycle" panel in the Plan Overview Panel (Workspace Settings)
 */
const BillingCycle: React.FCC<BillingCycleProps> = props => {
  const {
    subscriptionEnd,
    nextBillingCycleDate,
    seats,
    isWorkspaceOnTrial,
    isPartner,
    isPartneredWorkspace,
    onCancelSeatScheduleChange,
    customerHasPaymentDetails,
    currentPlan,
  } = props

  const isScheduled = !!seats.scheduledSeatsTotal
  const isEducationPlan = currentPlan?.product === 'STUDENT'
  const isNonPartnerInPartneredWorkspace = isPartneredWorkspace && !isPartner
  const partnerDescription =
    'Your subscription is scheduled to be canceled at the end of the current billing period. You can keep using the Workspace until'
  const userDescription = 'You can keep using the Workspace until'

  if (subscriptionEnd) {
    return (
      <BillingInfoTable.Row>
        <BillingInfoTable.HeadingCell>
          Next Billing Date
        </BillingInfoTable.HeadingCell>
        <BillingInfoTable.Cell data-testid="plan-next-cycle">
          {!isNonPartnerInPartneredWorkspace ? (
            <div>
              <ErrorPill data-testid="warning-subscription-badge">
                {isPartner ? 'Cancelation Scheduled' : 'Subscription Canceled'}
              </ErrorPill>
              <br />

              <span>
                {isPartner ? partnerDescription : userDescription}{' '}
                {dateFormat(new Date(subscriptionEnd))}
              </span>
            </div>
          ) : (
            <span>
              <strong>{dateFormat(new Date(subscriptionEnd))}</strong>
            </span>
          )}
        </BillingInfoTable.Cell>
      </BillingInfoTable.Row>
    )
  }

  return (
    <BillingInfoTable.Row>
      <BillingInfoTable.HeadingCell>
        {getHeaderCopy(isWorkspaceOnTrial, isEducationPlan)}
      </BillingInfoTable.HeadingCell>
      <BillingInfoTable.Cell data-testid="plan-next-cycle">
        <Description>
          <strong>{dateFormat(new Date(nextBillingCycleDate!))}</strong>
          <br />
          {isNonPartnerInPartneredWorkspace && (
            <>This Workspace is managed by the Sketch Partner.</>
          )}

          {!isNonPartnerInPartneredWorkspace &&
            isWorkspaceOnTrial &&
            (customerHasPaymentDetails ? (
              <>
                You&apos;ll be billed{' '}
                {PLAN_TYPE_LABEL[currentPlan!.type].toLowerCase()} for Editor
                Seats you&apos;re using at the end of your trial.
                <br />
                <br />
              </>
            ) : (
              <>If you subscribe, you&#39;ll be billed on this date</>
            ))}

          {!isNonPartnerInPartneredWorkspace &&
            !isWorkspaceOnTrial &&
            isScheduled &&
            !isEducationPlan && (
              <>
                You have scheduled a change to have {seats.scheduledSeatsTotal}{' '}
                Editor seats on the date above.{' '}
                <StyledButton onClick={onCancelSeatScheduleChange}>
                  Cancel scheduled change?
                </StyledButton>
                <br />
                <br />
              </>
            )}
          {!isNonPartnerInPartneredWorkspace &&
            isEducationPlan &&
            customerHasPaymentDetails && (
              <>
                You&apos;ll be billed for{' '}
                {seats.scheduledSeatsTotal ?? seats.currentSeatsTotal} Editor{' '}
                <Pluralize
                  singular="Seat"
                  plural="Seats"
                  count={seats.scheduledSeatsTotal ?? seats.currentSeatsTotal}
                  wrapperElement={React.Fragment}
                />{' '}
                at the end of your Education plan.
                <br />
                <br />
              </>
            )}
        </Description>
      </BillingInfoTable.Cell>
    </BillingInfoTable.Row>
  )
}

export default BillingCycle
